/**
 * routeMeta.js — runtime per-route <head> tags + route scroll handling (spec §2, §8.1, §8.2).
 *
 * Called ONLY from MarketingRoutes' effect in src/App.js, so the portal (/app/*, portal hosts) and the
 * admin console never run it. public/index.html is shared by every host, which is why canonical,
 * robots and JSON-LD are set here at runtime and never in the static head.
 */

const SITE = 'https://keepmeout.app';

const HOME_DESCRIPTION =
  'Lock your Android phone for a set time with Keep Me Out, or add a gentle pause before distracting apps with Bring Me Back on iPhone and Android.';

const ROUTE_META = {
  '/': {
    title: 'Keep Me Out – Lock Your Phone & Break Screen Time Habits',
    description: HOME_DESCRIPTION,
    canonical: `${SITE}/`,
    noindex: false,
    jsonLd: true,
  },
  '/bring-me-back': {
    title: 'Bring Me Back – A Gentle Pause Before Distracting Apps',
    description:
      'Bring Me Back adds a short, mindful pause before the apps you choose and blocks adult content on your phone. Free to download for iPhone and Android.',
    canonical: `${SITE}/bring-me-back`,
    noindex: false,
  },
  '/Help': {
    title: 'Help & FAQ – Keep Me Out',
    description: 'Answers to common questions about locking your phone with Keep Me Out.',
    canonical: `${SITE}/Help`,
    noindex: false,
  },
  '/Privacy': {
    title: 'Privacy Policy – Keep Me Out',
    description: 'How Keep Me Out handles your data.',
    canonical: `${SITE}/Privacy`,
    noindex: false,
  },
  '/extension': {
    title: 'Browser Extension – Keep Me Out',
    description: 'The Keep Me Out browser extension is coming soon.',
    canonical: `${SITE}/extension`,
    noindex: true,
  },
};

const NOT_FOUND_META = {
  title: 'Page not found – Keep Me Out',
  description: HOME_DESCRIPTION,
  canonical: null,
  noindex: true,
};

// No aggregateRating, no review, no FAQPage, no founder, no BMB publisher (spec §8.2).
export const JSON_LD = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'Organization',
      '@id': 'https://keepmeout.app/#org',
      name: 'eudaiTec',
      legalName: 'eudaiTec GmbH',
      url: 'https://eudaitec.com/',
      email: 'keepmeout.help@eudaitec.com',
      address: { '@type': 'PostalAddress', addressLocality: 'Berlin', addressCountry: 'DE' },
      sameAs: [
        'https://play.google.com/store/apps/dev?id=7477744655830297468',
        'https://www.facebook.com/keepmeoutapp',
      ],
    },
    {
      '@type': 'WebSite',
      '@id': 'https://keepmeout.app/#website',
      url: 'https://keepmeout.app/',
      name: 'Keep Me Out',
      publisher: { '@id': 'https://keepmeout.app/#org' },
    },
    {
      '@type': 'MobileApplication',
      '@id': 'https://keepmeout.app/#keep-me-out',
      name: 'Keep Me Out',
      operatingSystem: 'Android',
      applicationCategory: 'UtilitiesApplication',
      installUrl: 'https://play.google.com/store/apps/details?id=vikesh.dass.lockmeout',
      offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
      publisher: { '@id': 'https://keepmeout.app/#org' },
    },
    {
      '@type': 'MobileApplication',
      '@id': 'https://keepmeout.app/#bring-me-back',
      name: 'Bring Me Back',
      alternateName: 'BringMeBack',
      operatingSystem: 'iOS, Android',
      applicationCategory: 'LifestyleApplication',
      installUrl: [
        'https://apps.apple.com/app/id6759518742',
        'https://play.google.com/store/apps/details?id=com.kiaanconsulting.slapmyhand',
      ],
      offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
    },
  ],
};

/** Exact, case-sensitive pathname match (routes are case-sensitive). */
export function metaForPath(pathname) {
  return Object.prototype.hasOwnProperty.call(ROUTE_META, pathname) ? ROUTE_META[pathname] : NOT_FOUND_META;
}

function upsertMetaName(name, content) {
  let el = document.head.querySelector(`meta[name="${name}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute('name', name);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function removeAll(selector) {
  document.head.querySelectorAll(selector).forEach((el) => el.parentNode.removeChild(el));
}

export function applyRouteMeta(pathname) {
  if (typeof document === 'undefined' || !document.head) return;
  const meta = metaForPath(pathname);

  document.title = meta.title;
  upsertMetaName('description', meta.description);

  if (meta.canonical) {
    let link = document.head.querySelector('link[rel="canonical"]');
    if (!link) {
      link = document.createElement('link');
      link.setAttribute('rel', 'canonical');
      document.head.appendChild(link);
    }
    link.setAttribute('href', meta.canonical);
  } else {
    removeAll('link[rel="canonical"]');
  }

  if (meta.noindex) upsertMetaName('robots', 'noindex');
  else removeAll('meta[name="robots"]');

  if (meta.jsonLd) {
    let script = document.getElementById('mk-jsonld');
    if (!script) {
      script = document.createElement('script');
      script.type = 'application/ld+json';
      script.id = 'mk-jsonld';
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(JSON_LD);
  } else {
    const script = document.getElementById('mk-jsonld');
    if (script) script.parentNode.removeChild(script);
  }
}

// The previous marketing pathname: null on first load, so a reload keeps the browser's own scroll
// restoration. (MarketingRoutes mounts once, so module state behaves like a useRef there.)
let previousPathname = null;

export function handleRouteScroll(location) {
  if (typeof window === 'undefined') return;
  const { pathname, hash } = location;
  if (hash) {
    const id = decodeURIComponent(hash.slice(1));
    window.requestAnimationFrame(() => {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ block: 'start' });
    });
  } else if (previousPathname !== null && previousPathname !== pathname) {
    window.scrollTo(0, 0);
  }
  previousPathname = pathname;
}
